<template>
  <div>
    <app-cells><h1 class="title">Fuel receipts</h1></app-cells>
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <div v-if="!fuel_receipts.length" class="no-results">
        <img src="@/assets/img/schedule-plug-icon.svg" alt="">
        <div class="no-results__title">No missing receipts</div>
        <div class="no-results__text">
          If you just refilled your car and the receipt doesn't appear, please wait 5 minutes for system synchronization
        </div>
      </div>
      <div v-else class="info-cards">
        <button
          v-for="receipt of fuel_receipts"
          :key="receipt.id"
          class="info-cards__card"
          :class="{ 'info-cards__card--active': receipt.supplier }"
          :disabled="!receipt.supplier"
          @click="onShowModal(receipt)"
        >
          <span class="info-cards__row"><span>Date:</span><span>{{ dateTimeFormat(receipt.date) }}</span></span>
          <span class="info-cards__row"><span>Vendor:</span><span>{{ receipt.vendor.name || '-' }}</span></span>
          <span class="info-cards__row"><span>Car:</span><span>{{ receipt.car.plate_number }} ({{ formatString(receipt.odometer) }} km)</span></span>
          <span class="info-cards__row"><span>Amount:</span><span>{{ formatString(receipt.amount) }} TZS ({{ receipt.fuel_type }})</span></span>
          <span v-if="!receipt.supplier" class="status-tag status-tag--error">
            Missing supplier for Vendor {{ receipt.vendor.number }}, contact transfer department
          </span>
        </button>
      </div>
      <app-cells v-if="pagination.pages > 1" position="center">
        <paginate
          :clickHandler="onClickPagination"
          v-model="pagination.page"
          :page-count="pagination.pages"
          :prev-text="'<'"
          :next-text="'>'"
          :container-class="'pagination'"
          :page-class="'pagination__item'"
          :page-link-class="'pagination__link'"
          :prev-class="'pagination__item pagination__item--prev'"
          :next-class="'pagination__item pagination__item--next'"
          :next-link-class="'pagination__link pagination__link--next'"
          :prev-link-class="'pagination__link pagination__link--prev'"
        />
      </app-cells>
    </template>
    <modal
      name="receipt-modal"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      :shiftY="0"
      transition="pop-out"
    >
      <div class="popup">
        <button @click="$modal.hide('receipt-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Post receipt for {{ formatString(receipt_to_action?.amount) }} TZS</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-form-group label="EFD Verification Code" label-for="code" required>
              <app-input
                v-model="form.verification_code"
                @input.native="onInputCode"
                :error="$v.form.verification_code.$error"
                id="code"
                placeholder="EFD Verification Code"
                name="code"
              />
              <template #error>
                <div v-if="$v.form.verification_code.$dirty && !$v.form.verification_code.required">Required field</div>
                <div v-if="$v.form.verification_code.$dirty && !$v.form.verification_code.alphaNum">
                  Accepts only alphanumerics
                </div>
              </template>
            </app-form-group>
            <app-cells :indent="false" :wrap="false">
              <app-form-group label="EFD Date" required style="flex:1">
                <date-picker
                  v-model="form.date"
                  format="DD MMM YYYY"
                  value-type="YYYY-MM-DD"
                  placeholder="Date"
                  title-format="DD MMM YYYY"
                  class="datepicker"
                  :class="{'datepicker--error': $v.form.date.$error}"
                  popup-class="datepicker__popup"
                  :lang="lang"
                />
                <template #error>
                  <div v-if="$v.form.date.$dirty && !$v.form.date.required">Required field</div>
                </template>
              </app-form-group>
              <app-form-group label="EFD Time" required style="flex:1">
                <app-input
                  v-model="form.time"
                  v-mask="'99:99:99'"
                  placeholder="00:00:00"
                  autocomplete="off"
                  id="time"
                  name="time"
                  inputmode="numeric"
                  @paste.native.prevent
                  :error="$v.form.time.$error"
                />
                <template #error>
                  <div v-if="$v.form.time.$dirty && !$v.form.time.timeRegex">Wrong time</div>
                  <div v-if="$v.form.time.$dirty && !$v.form.time.required">Required field</div>
                </template>
              </app-form-group>
            </app-cells>
            <app-form-group label="EFD Receipt" required>
              <app-uploader
                v-model="form.receipt_copy"
                :extensions="['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/zip', 'application/pdf', 'image/jpeg', 'image/png', 'image/tiff', 'image/bmp']"
                formDataFormats="DOC,DOCX,XLS,XLSX,ZIP,PDF,JPEG,BMP,PNG,TIFF"
              />
              <template #error>
                <div v-if="$v.form.receipt_copy.$dirty && !$v.form.receipt_copy.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Price per litre" label-for="price" required>
              <app-fin-number
                v-model="form.price_per_litre"
                :error="$v.form.price_per_litre.$error"
                id="price"
                placeholder="0"
                name="price"
                inputmode="numeric"
              />
              <template #error>
                <div v-if="$v.form.price_per_litre.$dirty && !$v.form.price_per_litre.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Payment Receipt" required>
              <app-uploader
                v-model="form.payment_receipt"
                :extensions="['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/zip', 'application/pdf', 'image/jpeg', 'image/png', 'image/tiff', 'image/bmp']"
                formDataFormats="DOC,DOCX,XLS,XLSX,ZIP,PDF,JPEG,BMP,PNG,TIFF"
              />
              <template #error>
                <div v-if="$v.form.payment_receipt.$dirty && !$v.form.payment_receipt.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="submit">Send</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { format, parseISO } from 'date-fns'
import { alphaNum, required, helpers } from 'vuelidate/lib/validators'
import { getCarManagementFuelLogsDrivers, patchCarManagementFuelLogsEfdReceipts } from '@/http'

const timeRegex = helpers.regex('timeRegex', /^([0-1][0-9]|2[0-3]):([0-5][0-9]):([0-5][0-9])?$/i)

export default {
  name: 'TransfersFuelReceipts',
  data() {
    return {
      page_loaded: false,
      fuel_receipts: [],
      form: {},
      lang: {
        formatLocale: {
          months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
          monthsShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
          weekdays: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
          weekdaysShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
          weekdaysMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
          firstDayOfWeek: 1,
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      receipt_to_action: null,
    }
  },
  validations: {
    form: {
      verification_code: {
        required,
        alphaNum,
      },
      date: { required },
      time: { required, timeRegex },
      price_per_litre: { required },
      receipt_copy: { required },
      payment_receipt: { required },
    },
  },
  created() {
    this.fetchItems()
  },
  methods: {
    async fetchItems() {
      const response = await getCarManagementFuelLogsDrivers({ page: this.pagination.page, page_size: 5 })
      this.fuel_receipts = response.data.results
      this.pagination.pages = response.data.pages
      this.pagination.count = response.data.count
      this.page_loaded = true
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      patchCarManagementFuelLogsEfdReceipts(
        this.receipt_to_action.id,
        { ...this.form,
          receipt_copy: this.form.receipt_copy.id,
          payment_receipt: this.form.payment_receipt.id,
        }
      )
        .then(() => {
          document.body.scrollIntoView()
          this.fetchItems()
          this.$notify({
            type: 'success',
            title: 'Receipt added',
          })
          this.$modal.hide('receipt-modal')
        })
        .catch(() => { this.$refs.submit.preload = false })
    },
    onShowModal(receipt) {
      this.receipt_to_action = receipt
      this.form = {
        date: receipt.date.slice(0, 10)
      }
      this.$v.form.$reset()
      this.$modal.show('receipt-modal')
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
    formatString(string) {
      return string ? new Intl.NumberFormat('en-US').format(string) : '-'
    },
    onInputCode() {
      this.form.verification_code = this.form.verification_code.toUpperCase()
    },
  }
}
</script>
